import type { PipeTransform } from '@angular/core'
import { inject, Injectable, Pipe } from '@angular/core'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { User } from '@awork/features/user/models/user.model'
import type { IUserBase } from '@awork/features/user/models/user.model.base'

@Pipe({ name: 'userDisplayName' })
@Injectable({ providedIn: 'root' })
export class UserDisplayNamePipe implements PipeTransform {
  private readonly settingsQuery = inject(SettingsQuery)

  /**
   * Returns the name format selected by the current viewer.
   * @param {User | IUserBase | null | undefined} user User to display.
   * @returns {string} The viewer-specific display name.
   */
  transform(user: User | IUserBase | null | undefined): string {
    if (!user) {
      return ''
    }

    const fullNameOrFallback = this.getFullNameOrFallback(user)
    const firstName = user.firstName || ''

    if (this.settingsQuery.getUserNameDisplaySetting() === 'first_name') {
      return firstName || fullNameOrFallback
    }

    return fullNameOrFallback
  }

  /**
   * Builds a full name or fallback from either a User instance or an API user object.
   * @param {User | IUserBase} user User to read.
   * @returns {string} The full name fallback.
   */
  private getFullNameOrFallback(user: User | IUserBase): string {
    if (user instanceof User) {
      return user.fullName
    }

    const availableName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()

    if (availableName) {
      return availableName
    }

    const noName = (q.translations.common as { noName?: string }).noName

    return noName || new User({ ...user }, 'UserDisplayNamePipe').fullName
  }
}
